<script lang="ts">
  import { onMount } from "svelte";
  import { type CreateServerInput } from "@/api";
  import { routeHref, navigate } from "@/router";
  import {
    balance,
    servers,
    serverLimit,
    busyAction,
    nodeCapacityAvailable,
    refreshCapacity,
    createServer,
    purchaseServerSlot,
  } from "@/stores/dashboard";
  import CreateServerForm from "@/components/CreateServerForm.svelte";

  onMount(() => {
    const controller = new AbortController();
    void refreshCapacity(controller.signal).catch(() => {});
    return () => controller.abort();
  });

  async function submit(input: CreateServerInput): Promise<boolean> {
    const created = await createServer(input);
    if (created) {
      navigate("server-home", { id: created.id });
    }
    return created !== null;
  }
</script>

<header class="page-intro">
  <a class="back-link" href={routeHref("servers")}>← Все серверы</a>
  <p class="eyebrow">Серверы / конфигурация</p>
  <h1>Новый сервер</h1>
  <p class="intro-copy">
    Выберите игру и сборку, проверьте стоимость — затем мы установим и запустим сервер.
  </p>
</header>

<div class="form-layout">
  <CreateServerForm
    serverCount={$servers.length}
    serverLimit={$serverLimit}
    balance={$balance}
    nodeCapacityAvailable={$nodeCapacityAvailable}
    submitting={$busyAction === "create-server"}
    slotBuying={$busyAction === "server-slot"}
    onSubmit={submit}
    onPurchaseSlot={purchaseServerSlot}
  />

  <aside class="setup-aside" aria-label="Как создаётся сервер">
    <div class="aside-orbit" aria-hidden="true"><span></span></div>
    <p class="eyebrow">Маршрут запуска</p>
    <h2>От идеи<br />до игры.</h2>
    <ol class="setup-steps">
      <li>
        <span>01</span>
        <div>
          <strong>Игра и название</strong><small
            >Укажите, как сервер будет называться в панели.</small
          >
        </div>
      </li>
      <li>
        <span>02</span>
        <div>
          <strong>Сборка и версия</strong><small>Выберите поддержку плагинов или модов.</small>
        </div>
      </li>
      <li>
        <span>03</span>
        <div>
          <strong>Проверка и запуск</strong><small
            >Проверьте параметры и стоимость перед созданием.</small
          >
        </div>
      </li>
    </ol>
    <p class="aside-note">
      Обычно установка занимает несколько минут. Этапы установки появятся на странице сервера.
    </p>
  </aside>
</div>

<style>
  .page-intro {
    max-width: 47rem;
    margin-bottom: 2.75rem;
  }

  .page-intro h1 {
    max-width: 16ch;
    margin: 0.9rem 0 0.8rem;
    font: 500 clamp(2rem, 5vw, 3.6rem)/1 var(--font-display);
    letter-spacing: -0.06em;
    overflow-wrap: anywhere;
    text-transform: uppercase;
  }

  .form-layout {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(15rem, 0.65fr);
    align-items: start;
    gap: clamp(2rem, 7vw, 6rem);
  }

  .setup-aside {
    position: sticky;
    top: 6.5rem;
    max-width: 20rem;
    padding: 1rem 0;
  }

  .aside-orbit {
    position: relative;
    display: grid;
    width: 4.6rem;
    aspect-ratio: 1;
    margin-bottom: 2rem;
    place-items: center;
    border: 1px dashed rgb(240 112 79 / 0.65);
    border-radius: 50%;
    rotate: -18deg;
  }

  .aside-orbit::before {
    position: absolute;
    width: 5.5rem;
    height: 1.35rem;
    border: 1px solid var(--line-strong);
    border-radius: 50%;
    content: "";
  }

  .aside-orbit::after {
    position: absolute;
    top: 0.05rem;
    right: -0.15rem;
    width: 0.35rem;
    aspect-ratio: 1;
    border-radius: 50%;
    background: var(--cream);
    content: "";
  }

  .aside-orbit span {
    width: 1.35rem;
    aspect-ratio: 1;
    border-radius: 50%;
    background: var(--mars);
    box-shadow: 0 0 24px rgb(214 75 50 / 0.5);
  }

  .setup-aside h2 {
    margin: 0.8rem 0 2rem;
    color: var(--white);
    font: 500 clamp(1.7rem, 3vw, 2.6rem)/1.05 var(--font-display);
    letter-spacing: -0.065em;
    text-transform: uppercase;
  }

  .setup-steps {
    display: grid;
    gap: 1.35rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .setup-steps li {
    display: grid;
    grid-template-columns: 2.2rem 1fr;
    gap: 0.7rem;
    align-items: start;
  }

  .setup-steps li > span {
    color: var(--mars-light);
    font: 600 0.65rem/1 var(--font-display);
    letter-spacing: 0.08em;
  }

  .setup-steps div {
    display: grid;
    gap: 0.35rem;
  }

  .setup-steps strong {
    color: var(--cream);
    font-size: 0.78rem;
  }

  .setup-steps small,
  .aside-note {
    color: var(--dim);
    font-size: 0.72rem;
    line-height: 1.55;
  }

  .setup-steps small {
    max-width: 15rem;
  }

  .aside-note {
    margin: 2rem 0 0;
    padding-top: 1rem;
    border-top: 1px solid var(--line);
  }

  @media (max-width: 760px) {
    .form-layout {
      grid-template-columns: 1fr;
    }

    .setup-aside {
      position: static;
      display: none;
    }
  }
</style>
